/**
 * Componente TaskDistributionChart
 * 
 * Gráfico de distribuição de tarefas por tipo.
 * Wrapper específico para o gráfico de pizza de distribuição de tarefas.
 * 
 * @module components/Dashboard/ChartsSection/TaskDistributionChart
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FaChartPie } from 'react-icons/fa';
import GraficoPizza from '../../Charts/GraficoPizza';
import ChartWrapper from './ChartWrapper';
import chartConfig from '../../../utils/dashboard/chartConfig';

/**
 * Componente de gráfico de distribuição de tarefas
 * 
 * Renderiza um gráfico de pizza mostrando a distribuição de tarefas por tipo.
 * 
 * @param {object} props - Propriedades do componente
 * @param {Array} props.data - Dados da distribuição de tarefas
 * @param {boolean} props.isLoading - Indica se está carregando
 * @param {Error|string|null} props.error - Erro ocorrido
 * @param {string} [props.periodo] - Período dos dados (ex: "30 Dias")
 * @param {string} [props.className] - Classes CSS adicionais
 * @param {string} [props.testId] - ID para testes
 * 
 * @returns {JSX.Element} Componente de gráfico
 * 
 * @example
 * ```jsx
 * <TaskDistributionChart
 *     data={taskDistribution}
 *     isLoading={isLoading}
 *     error={error}
 *     periodo="30 Dias"
 * />
 * ```
 */
const TaskDistributionChart = ({
    data,
    isLoading,
    error,
    periodo = '30 Dias',
    className = '',
    testId = null,
}) => {
    const title = (
        <>
            <FaChartPie className="mr-2 text-indigo-500 inline" />
            Distribuição de Tarefas ({periodo})
        </>
    );

    const subtitle = 'Análise da proporção de tarefas concluídas por tipo.';

    return (
        <ChartWrapper
            isLoading={isLoading}
            error={error}
            data={data}
            loadingMessage="Carregando distribuição de tarefas..."
            errorMessage="Erro ao carregar distribuição de tarefas."
            emptyMessage="Não há dados de distribuição de tarefas para exibir."
            className={className}
            testId={testId || 'task-distribution-chart'}
        >
            <GraficoPizza
                data={data}
                dataKey={chartConfig.taskDistributionConfig?.dataKey || 'total'}
                nameKey={chartConfig.taskDistributionConfig?.nameKey || 'tipo_tarefa'}
                title={title}
                subtitle={subtitle}
            />
        </ChartWrapper>
    );
};

TaskDistributionChart.propTypes = {
    /** Dados da distribuição de tarefas */
    data: PropTypes.array,
    
    /** Indica se está carregando */
    isLoading: PropTypes.bool.isRequired,
    
    /** Erro ocorrido (null se não houver) */
    error: PropTypes.oneOfType([
        PropTypes.instanceOf(Error),
        PropTypes.string,
        PropTypes.oneOf([null]),
    ]),
    
    /** Período dos dados */
    periodo: PropTypes.string,
    
    /** Classes CSS adicionais */
    className: PropTypes.string,
    
    /** ID para testes */
    testId: PropTypes.string,
};

TaskDistributionChart.defaultProps = {
    data: null,
    error: null,
    periodo: '30 Dias',
    className: '',
    testId: null,
};

export default TaskDistributionChart;

